import { useMemo, useState } from 'react';
import { useFetchUtilityExpensesReportQuery } from '../../api';
import { formatAmount } from '../../utils';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Typography } from '@mui/material';
import moment from 'moment';

const UtilityExpensesReport = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const params = useMemo(
    () => ({
      startDate: startDate ? startDate.format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: endDate ? endDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
    }),
    [startDate, endDate]
  );

  const { data, isLoading, isFetching } = useFetchUtilityExpensesReportQuery(params);

  const byCategoryEntries: [string, number][] = data?.byCategory ? Object.entries(data.byCategory) : [];
  const byTypeEntries: [string, number][] = data?.byType ? Object.entries(data.byType) : [];
  const items: any[] = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Typography variant="h6" className="font-bold text-slate-800">Utility Expenses Report</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex flex-wrap items-center gap-3">
            <DatePicker label="Start Date" value={startDate} onChange={setStartDate} slotProps={{ textField: { size: 'small' } }} />
            <DatePicker label="End Date" value={endDate} onChange={setEndDate} slotProps={{ textField: { size: 'small' } }} />
          </div>
        </LocalizationProvider>
      </div>

      {(isLoading || isFetching) ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-500 italic">Loading utility expenses…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryTile label="Total Amount" value={`RWF ${formatAmount(data?.totalAmount || 0)}`} accent="emerald" />
            <SummaryTile label="Entries" value={(data?.count || 0).toLocaleString()} />
            <SummaryTile
              label="Period"
              value={`${moment(data?.startDate).format('MMM D')} - ${moment(data?.endDate).format('MMM D, YYYY')}`}
              small
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BreakdownCard title="By Category" entries={byCategoryEntries} total={data?.totalAmount || 0} />
            <BreakdownCard title="By Type" entries={byTypeEntries} total={data?.totalAmount || 0} />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
              <h3 className="font-bold text-slate-800 text-sm">Utility Expense Items ({items.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Name</th>
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Category</th>
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Type</th>
                    <th className="py-3 px-4 text-right font-bold text-slate-700">Amount</th>
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Created By</th>
                    <th className="py-3 px-4 text-left font-bold text-slate-700">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-400">No expenses in this period</td>
                    </tr>
                  ) : items.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4 text-slate-800 font-medium">
                        <div>{item.name}</div>
                        {item.description && <div className="text-xs text-slate-500 mt-0.5">{item.description}</div>}
                      </td>
                      <td className="py-3 px-4 text-slate-700">{item.category}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${item.type === 'FIXED' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-900">RWF {formatAmount(item.amount)}</td>
                      <td className="py-3 px-4 text-slate-700">
                        {item.createdBy ? `${item.createdBy.firstName || ''} ${item.createdBy.lastName || ''}`.trim() || '-' : '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{moment(item.createdAt).format('MMM D, YYYY')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const SummaryTile = ({ label, value, accent, small }: { label: string; value: string; accent?: 'emerald'; small?: boolean }) => (
  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{label}</p>
    <p className={`${small ? 'text-base' : 'text-xl'} font-bold ${accent === 'emerald' ? 'text-emerald-600' : 'text-slate-800'}`}>
      {value}
    </p>
  </div>
);

const BreakdownCard = ({ title, entries, total }: { title: string; entries: [string, number][]; total: number }) => (
  <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
    <h3 className="font-bold text-slate-800 text-sm mb-4">{title}</h3>
    {entries.length === 0 ? (
      <p className="text-slate-400 text-sm">No data</p>
    ) : (
      <div className="space-y-3">
        {entries.map(([key, value]) => {
          const pct = total > 0 ? (value / total) * 100 : 0;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-slate-700">{key}</span>
                <span className="text-sm font-bold text-slate-900">RWF {formatAmount(value)}</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-800 rounded-full" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{pct.toFixed(1)}%</div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default UtilityExpensesReport;
